import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';

import SectionOrder from './SectionOrder';
import AntSwitch from '../../util/AntSwitch';
import { mislecAPI } from '../../../util/axios';
import { skillList } from '../../util/form-values';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
};

function Settings({ course, saveCourse, sectionVisibility, setRemoveCourseModal, setOpen, setMessage, setSeverity, user, updateUser }) {
    const [courseTitle, setCourseTitle] = React.useState(course?.title || '');
    const [courseLanguage, setCourseLanguage] = React.useState(course?.language || '');
    const [courseTags, setCourseTags] = React.useState(course?.tags || []);
    const [published, setPublished] = React.useState(course?.published || false);

    const navigate = useNavigate();
    const { i18n, t } = useTranslation();

    const metadata = () => {
        //  Data preparation
        const data = {
            title: courseTitle,
            language: courseLanguage,
            tags: courseTags,
            published: published
        }

        //  Send data to server
        saveCourse(data);
    }

    const reorder = (order) => {
        //  Data preparation
        const data = {
            order: order
        }

        //  Send data to server
        saveCourse(data);
    }

    const clone = () => {
        const cloned = course;

        //  Data preparation
        cloned.user = user._id;
        cloned.timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
        cloned.published = false;

        //  Send data to server
        mislecAPI.post(`/api/course`, cloned)
            .then(res => {
                //  Notify user of success
                setOpen(true);
                setMessage(t(res.data.detail));
                setSeverity('success');

                setTimeout(() => {
                    //  Redirect to the new course
                    navigate(`/course/${res.data.id}`);
                    updateUser(null);
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    return (
        <>
            <Grid container sx={{ ml: 1, pr: 2 }}>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2, pt: 0 }}>
                    <Grid item xs={12} sx={{ pt: '0px !important' }}>
                        <Typography variant='h5'>{t('course.tabs.settings.information.title')}</Typography>
                        <Typography variant='body2' sx={{ mt: 1 }}>
                            {t('course.tabs.settings.information.description')}
                        </Typography>
                    </Grid>
                    <Grid item md={9} xs={12} sx={{ pt: '32px !important' }}>
                        <TextField
                            id='mISLec_course_title'
                            name='title'
                            label={t('course.tabs.settings.inputs.title')}
                            value={courseTitle}
                            onChange={(e) => setCourseTitle(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item md={3} xs={12} sx={{ pt: '32px !important' }}>
                        <FormControl fullWidth>
                            <InputLabel id='mISLec_course_language_label'>{t('course.tabs.settings.information.inputs.language.label')}</InputLabel>
                            <Select
                                id='mISLec_course_language'
                                labelId='mISLec_course_language_label'
                                label={t('course.tabs.settings.information.inputs.language.label')}
                                value={courseLanguage}
                                onChange={(e) => setCourseLanguage(e.target.value)}
                            >
                                {Object.keys(i18n.options.resources).map((lng) => (
                                    <MenuItem
                                        value={lng}
                                        key={lng}
                                    >
                                        <ReactCountryFlag
                                            countryCode={lng.split('-')[1]}
                                            svg
                                            style={{ fontSize: '1.2em', width: '1.2em', borderRadius: '4px' }}
                                        />

                                        <Typography variant='body2' sx={{ display: 'inline', color: 'text.primary', fontSize: '0.8rem', ml: 1 }}>
                                            {t('header.language.' + lng)}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: { xs: 'inherit', md: '32px !important' } }}>
                        <FormControl fullWidth>
                            <InputLabel id='mISLec_course_tags'>{t('course.tabs.settings.information.inputs.tags.label')}</InputLabel>
                            <Select
                                id='mISLec_course_tags'
                                labelId='mISLec_course_tags'
                                label={t('course.tabs.settings.information.inputs.tags.label')}
                                value={courseTags}
                                onChange={(e) => setCourseTags(e.target.value)}
                                multiple
                                renderValue={(selected) => {
                                    const displayLimit = 20;
                                    const displayItems = selected.slice(0, displayLimit);
                                    const hiddenCount = selected.length - displayLimit;

                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                alignItems: 'center'
                                            }}
                                        >
                                            {displayItems.map((skill) => (
                                                <Chip key={skill} label={t(`profile.tabs.profile.basic.inputs.skills.values.${skill}`)} />
                                            ))}
                                            {hiddenCount > 0 && (
                                                <span style={{ fontSize: '0.8125rem' }}>{`+${hiddenCount} ${t('profile.tabs.profile.basic.inputs.skills.more')}`}</span>
                                            )}
                                        </Box>
                                    );
                                }}
                                MenuProps={MenuProps}
                            >
                                {skillList.map((skill, index) => (
                                    <MenuItem key={index} value={skill}>
                                        <Typography variant='body2'>{t(`profile.tabs.profile.basic.inputs.skills.values.${skill}`)}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            alignItems='start'
                            wrap='nowrap'
                            onClick={() => setPublished(!published)}
                            sx={{
                                display: 'inline-flex',
                                cursor: 'pointer',
                                p: 0.5,
                                fontSize: '0.8rem',
                                width: 'auto',
                                '&:hover': {
                                    backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                    borderRadius: '8px'
                                }
                            }}
                        >
                            <Grid item sx={{ mr: 1, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                <AntSwitch checked={published} onChange={() => setPublished(!published)} />
                            </Grid>
                            <Grid item>
                                <Typography variant='body2' sx={{ fontWeight: '500' }}>
                                    {t('course.tabs.settings.information.inputs.published.label')}
                                </Typography>
                                <Typography variant='caption' color='text.secondary'>
                                    {t('course.tabs.settings.information.inputs.published.description')}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, pt: '16px !important' }}>
                                <Button
                                    variant=''
                                    onClick={() => metadata()}
                                    sx={{
                                        borderRadius: '8px !important',
                                        minWidth: {
                                            xs: '100%',
                                            md: 180
                                        },
                                        maxWidth: {
                                            xs: '100%',
                                            md: 180
                                        },
                                        backgroundColor: (theme) => ({
                                            xs: alpha(theme.palette.primary.main, 0.08),
                                            md: 'rgba(33, 43, 54, 0.08)'
                                        }),
                                        color: {
                                            xs: 'primary.main',
                                            md: 'inherit'
                                        },
                                        '&:hover': {
                                            color: 'primary.main',
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                        }
                                    }}
                                >
                                    {t('course.tabs.settings.buttons.save')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2, mt: 0, pt: '32px !important' }}>
                        <Divider />
                    </Grid>
                    {course?.sections?.length > 0 && <SectionOrder course={course} saveCourse={reorder} sectionVisibility={sectionVisibility} setOpen={setOpen} setMessage={setMessage} setSeverity={setSeverity} updateUser={updateUser} />}
                    <Grid item xs={12} sx={{ pt: '0px !important' }}>
                        <Typography variant='h5'>{t('course.tabs.settings.clone.title')}</Typography>
                        <Typography variant='body2' sx={{ mt: 1 }}>
                            {t('course.tabs.settings.clone.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, pt: '16px !important' }}>
                        <Button
                            variant=''
                            onClick={() => clone()}
                            sx={{
                                borderRadius: '8px !important',
                                minWidth: {
                                    xs: '100%',
                                    md: 180
                                },
                                maxWidth: {
                                    xs: '100%',
                                    md: 180
                                },
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.primary.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            {t('course.tabs.settings.buttons.clone')}
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mb: 2, mt: 0, pt: '16px' }}>
                    <Divider />
                </Grid>
                <Grid item xs={12} sx={{ pt: '0px !important' }}>
                    <Typography variant='h5'>{t('course.tabs.settings.delete.title')}</Typography>
                    <Typography variant='body2' sx={{ mt: 1 }}>
                        {t('course.tabs.settings.delete.description')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', pt: '16px !important' }} >
                    <Stack
                        sx={{
                            width: {
                                xs: '100%',
                                md: 180
                            }
                        }}
                    >
                        <Button
                            variant=''
                            fullWidth
                            onClick={() => setRemoveCourseModal(true)}
                            sx={{
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                                color: {
                                    xs: 'error.main'
                                },
                                '&:hover': {
                                    color: 'error.main',
                                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                                }
                            }}
                        >
                            {t('course.tabs.settings.buttons.delete')}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </>
    );
}

export default Settings;